import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Select from 'components/Common/Form/Input/Select'
import TextArea from 'components/Common/Form/Input/TextArea'
import { getData as getCountries } from 'country-list'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { useAppDispatch } from 'hooks/reduxHooks'
import { sendWaitListTourSoldOut } from 'actions/tour/TourV2Actions'
import TextLink from 'LuxKit/TextLink'
import config from 'constants/config'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { FormattedMessage, useIntl } from 'react-intl'

const {
  termsAndConditions,
  privacyPolicy,
} = config.contentLinks

const FormFieldsContainer = styled.div`
  margin-top: ${rem(16)};
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-column-gap: ${rem(12)};
    grid-template-columns: 1fr 1fr;

    .full-width-column {
      grid-column: span 2;
    }
  }
`

const OccupancyContainer = styled.div`
  margin-top: ${rem(4)};
`

const CommentField = styled(TextArea)`
  margin-top: ${rem(20)};
`

const TermsAndConditionsField = styled(CheckboxInput)`
  margin-top: ${rem(20)};
`

const ROOM_CAPACITIES: Array<App.RoomCapacity> = [{
  adults: 20,
  children: 10,
  infants: 0,
  teenagers: 0,
}]

interface MappedStateProps {
  firstName?: string
  lastName?: string
  phone?: string
  phonePrefix?: string
  email?: string
  countryCode: string
}
interface Props {
  offer: App.Tours.TourV2Offer
  departure?: App.Tours.TourV2OfferDeparture
}

function WaitingListRequestFormModal(props: Props & MappedStateProps) {
  const intl = useIntl()
  const {
    firstName,
    lastName,
    phone,
    phonePrefix,
    countryCode,
    email,
    offer,
    departure,
  } = props

  const dispatch = useAppDispatch()

  const maxChildAge = useMemo(() => Object.values(offer.variations)[0].maxChildPriceAge, [offer.variations])
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>([{
    adults: 2,
    children: 0,
  }])
  const handleOccupancies = useCallback((occupancies: Array<App.Occupants>) => setOccupancies(occupancies), [setOccupancies])
  const tourUrl = useMemo(() => window.location.href, [])

  const modalState = useModalElementContext<unknown>()

  const sendRequest = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const requestWaitingListTour: App.Tours.WaitingListTourRequest = formToObject(e.currentTarget)
    const fullPayload: App.Tours.WaitingListTourRequest = {
      ...requestWaitingListTour,
      occupancy: occupancies.map(occupant => ({
        adults: occupant.adults,
        children: occupant.children,
        childrenAge: occupant.childrenAge,
      })),
    }
    dispatch(sendWaitListTourSoldOut(fullPayload))
    modalState.resolve(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, occupancies])

  return <ModalBase size="S">
    <form onSubmit={sendRequest}>
      <ModalHeader title={intl.formatMessage({
        id: 'iWoUJ67XjV',
        defaultMessage: 'Join the waitlist',
        description: 'Modal title for the tour waitlist request form',
      })}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">
            {intl.formatMessage({
              id: '7clVlhnkEg',
              defaultMessage: 'Please let us know your details and our Tours Concierge will let you know if a spot on this tour becomes available.',
              description: 'Description text in the tour waitlist request form modal',
            })}
          </BodyText>
          <FormFieldsContainer>
            <HiddenInput name="tourId" value={offer.id} />
            <HiddenInput name="tourUrl" value={tourUrl} />
            {
              !!departure &&
              <>
                <HiddenInput name="departureStartDate" value={departure.startDate} />
                <HiddenInput name="departureEndDate" value={departure.endDate} />
              </>
            }
            <TextInput
              label={intl.formatMessage({
                id: 'FoHq2Pr7Ue',
                defaultMessage: 'First name',
                description: 'Label for first name input in tour waitlist request form',
              })}
              name="firstName"
              required
              placeholder={intl.formatMessage({
                id: '9U/XhEYdVi',
                defaultMessage: 'First name',
                description: 'Placeholder for first name input in tour waitlist request form',
              })}
              defaultValue={firstName}
            />
            <TextInput
              label={intl.formatMessage({
                id: 'CXcHhCtATT',
                defaultMessage: 'Last name',
                description: 'Label for last name input in tour waitlist request form',
              })}
              name="lastName"
              required
              placeholder={intl.formatMessage({
                id: '8CQxb0ZEKE',
                defaultMessage: 'Last name',
                description: 'Placeholder for last name input in tour waitlist request form',
              })}
              defaultValue={lastName}
            />
            <TextInput
              className="full-width-column"
              type="email"
              label={intl.formatMessage({
                id: 'ro8exJUc19',
                defaultMessage: 'Email address',
                description: 'Label for email input in tour waitlist request form',
              })}
              name="email"
              required
              defaultValue={email}
            />
            <PhoneInput
              label={intl.formatMessage({
                id: 'XLVengxW3P',
                defaultMessage: 'Mobile number',
                description: 'Label for phone input in tour waitlist request form',
              })}
              name="phone"
              id="mobileNumber"
              required
              defaultPhoneNumber={phone}
              defaultPhonePrefix={phonePrefix}
              prefixCountry={countryCode}
            />
            <Select
              label={intl.formatMessage({
                id: 'nGlWjPNp0k',
                defaultMessage: 'Your home country',
                description: 'Label for country select in tour waitlist request form',
              })}
              name="countryCode"
              defaultValue={countryCode}
              required
              noValidationSpacing
            >
              {getCountries().map(country =>
                <option key={country.code} value={country.code}>{country.name}</option>,
              )}
            </Select>
            <BodyText className="full-width-column" weight="bold" variant="medium">
              {intl.formatMessage({
                id: 'ssuLXzizMf',
                defaultMessage: 'Who are you travelling with?',
                description: 'Heading for occupancy section in tour waitlist request form',
              })}
            </BodyText>
            <OccupancyContainer className="full-width-column">
              <OccupancyPicker
                compact
                name="occupancy"
                maxRooms={10}
                capacities={ROOM_CAPACITIES}
                occupancies={occupancies}
                childrenAgeLabel={intl.formatMessage({
                  id: '8LqhQcNZ3n',
                  defaultMessage: 'at time of departure',
                  description: 'Suffix label for children age in occupancy picker',
                })}
                maxChildAge={maxChildAge}
                onChange={handleOccupancies}
                enableMultiRoom={false}
                disableSaleUnit
              />
            </OccupancyContainer>
            <CommentField
              className="full-width-column"
              name="comment"
              noValidationSpacing
              resize="vertical"
              label={intl.formatMessage({
                id: 'jGyLRS7BgF',
                defaultMessage: 'Any other questions or comments?',
                description: 'Label for comment textarea in tour waitlist request form',
              })}
              placeholder={intl.formatMessage({
                id: '6DKLxtnpIf',
                defaultMessage: 'Leave a message',
                description: 'Placeholder for comment textarea in tour waitlist request form',
              })}
              maxLength={150}
            />
            <TermsAndConditionsField className="full-width-column" required size="large">
              <FormattedMessage
                id="NcVTHF0rvf"
                defaultMessage="By continuing, you agree to our <termsLink>Terms &amp; Conditions</termsLink> and <privacyLink>Privacy Policy</privacyLink>, and to subscribe to marketing communications for offers, alerts and services."
                description="Consent text for terms, privacy policy, and marketing subscription in tour waitlist request form"
                values={{
                  termsLink: (chunks: React.ReactNode) => (
                    <TextLink to={termsAndConditions.to} href={termsAndConditions.href} target="_blank" weight="regular">
                      {chunks}
                    </TextLink>
                  ),
                  privacyLink: (chunks: React.ReactNode) => (
                    <TextLink to={privacyPolicy.to} href={privacyPolicy.href} target="_blank" weight="regular">
                      {chunks}
                    </TextLink>
                  ),
                }}
              />
            </TermsAndConditionsField>
          </FormFieldsContainer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'j9JbsiKfUy',
            defaultMessage: 'Submit request',
            description: 'Primary button text to submit tour waitlist request form',
          }),
          type: 'submit',
        }}
      />
    </form>
  </ModalBase>
}

const mapStateToProps = (state: App.State): MappedStateProps => {
  return {
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    phone: state.auth.account.phone,
    phonePrefix: state.auth.account.phonePrefix,
    email: state.auth.account.email,
    countryCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(WaitingListRequestFormModal)
